import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Avatar, Grid } from "@mui/material";
import Layout from "../../../components/Layout/Layout";
import QueryPageTitleCard from "../../../components/QueryPageTitleCard/QueryPageTitleCard";
import CustomCard from "../../../components/CustomCard/CustomCard";
import CardTitleWithSubtitle from "../../../components/CardTitleWithSubtitle/CardTitleWithSubtitle";
import CustomButton, {
  CustomButtonProps,
  CustomButtonVariant,
} from "../../../components/CustomButton/CustomButton";
import Select from "../../../components/Select/Select";
import SelectSubsidiaries from "../../../components/SelectSubsidiary/SelectSubsidiary";
import { useDistributor } from "../../../contexts/distributor/DistributorContext";
import { useNavigate } from "react-router-dom";
import { routesConfig } from "../../../config/routes";
import { getFormattedTimestamp } from "../../../utils/dateFormatter";
import {
  initialFilters,
  useStorageContext,
} from "../../../contexts/storage/storage-provider";
import CalendarRange from "../../../components/CalendarRange/CalendarRange";
import { format } from "date-fns";
import {
  ICustomerCompany,
  IProduct,
} from "../../../modules/storage/models/storage-dto";
import MultipleSelectCheckbox from "../../../components/MultipleSelectCheckbox";

type Props = PropsWithChildren<{
  title: string;
}>;

const ProgramScheduleTable = React.memo<Props>(({ title, children }) => {
  const [products, setProducts] = useState<IProduct[]>([]);
  const [companies, setCompanies] = useState<ICustomerCompany[]>([]);
  const [codeProduct, setCodeProduct] = useState<string | null>(null);
  const [codeSubsidiary, setCodeSubsidiary] = useState<string | undefined>(
    undefined
  );
  const [codeDistributors, setCodeDistributors] = useState<string[]>([]);
  const [clearFilters, setClearFilters] = useState(false);

  const { subsidiaries, setSelectedDistributorsStoraged } = useDistributor();
  const {
    statusVendor,
    filters,
    setFilters,
    fetchList,
    downloadReport,
    getStorageProducts,
    getStorageCompanies,
    fetchWithClearFilters,
  } = useStorageContext();

  const navigate = useNavigate();

  const { hash } = window.location;
  const slug = hash.split("#")[1];

  const isDischarge = slug === routesConfig.STORAGE().RIVER_DISCHARGES;

  const getProductsData = useCallback(async () => {
    const response = await getStorageProducts();
    if (response?.length) {
      setProducts(response);
    }
  }, [getStorageProducts, setProducts]);

  const getCompaniesData = useCallback(async () => {
    const response = await getStorageCompanies();
    if (response?.length) {
      setCompanies(response);
    }
  }, [getStorageCompanies]);

  const handleRedirect = () => {
    switch (slug) {
      case routesConfig.STORAGE().RIVER_DISCHARGES:
        return navigate(routesConfig.STORAGE().CREATE_SCHEDULING);
      case routesConfig.STORAGE().PIPELINES:
        return navigate(routesConfig.STORAGE().CREATE_PUMPING_REQUEST);
      default:
        return null;
    }
  };

  const actionButtons: CustomButtonProps[] = [
    {
      title: "Baixar relatório",
      variant: CustomButtonVariant.CONTAINED_DOWNLOAD,
      onClick: downloadReport,
    },
    {
      title: `Novo pedido de ${isDischarge ? "descarga" : "entrega"}`,
      onClick: () => handleRedirect(),
    },
  ];

  const handleCompaniesChange = (values: string[]) => {
    setCodeDistributors(values);
    setSelectedDistributorsStoraged(values);

    setFilters((prevState) => ({
      ...prevState,
      customerCompanyId: values.join(","),
      page: 1,
    }));
  };

  const handleFetchList = () => {
    fetchList({ ...filters, page: 1 });
  };

  const handleClearFilters = () => {
    localStorage.setItem("multipleSelectedDistributor", JSON.stringify([]));
    setCodeDistributors([]);
    setSelectedDistributorsStoraged([]);

    setCodeProduct(null);
    setCodeSubsidiary(undefined);

    setFilters(initialFilters);

    setClearFilters(true);
  };

  useEffect(() => {
    getCompaniesData();
    getProductsData();
  }, []);

  useEffect(() => {
    handleFetchList();
  }, [statusVendor]);

  useEffect(() => {
    if (clearFilters) {
      fetchWithClearFilters();
      setClearFilters(false);
    }
  }, [clearFilters, fetchWithClearFilters]);

  return (
    <Layout
      enableMargin
      appBarProps={{
        notification: {
          onChange() {},
        },
        chat: {
          onChange() {},
        },
        settings: {
          onChange() {},
        },
        avatar: () => (
          <Avatar
            alt="Avatar"
            src="/images/avatar.png"
            sx={{
              width: 60,
              height: 60,
            }}
          />
        ),
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <QueryPageTitleCard title={title} />
        </Grid>

        <Grid item xs={12}>
          <CustomCard
            sx={{
              boxShadow: "none",
              border: "1px solid #DFE0EB",
              height: "100%",
              padding: 2.4,
            }}
          >
            <CardTitleWithSubtitle
              title="Tabela de programações"
              subtitle={getFormattedTimestamp(new Date())}
            />

            <Grid
              container
              justifyContent="flex-end"
              spacing={1}
              sx={{ mb: 2 }}
            >
              {actionButtons.map((item) => (
                <Grid item key={item.title}>
                  <CustomButton
                    title={item.title}
                    variant={item.variant}
                    onClick={item.onClick}
                    sx={{ textTransform: "unset" }}
                  />
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={3} lg={2.4}>
                <MultipleSelectCheckbox
                  label="Clientes"
                  value={codeDistributors}
                  onChange={handleCompaniesChange}
                  options={companies.map((c) => ({
                    value: c.id,
                    label: c.description,
                  }))}
                />
              </Grid>

              <Grid item xs={12} md={3} lg={2.4}>
                <Select
                  label="Produtos"
                  options={products.map((option) => ({
                    option,
                  }))}
                  getOptionItemLabel={(value) =>
                    value !== null ? value.description : ""
                  }
                  getSelectedOptionLabel={(value) =>
                    value ? value?.description : ""
                  }
                  onChange={(value) => {
                    setCodeProduct(value?.id ?? null);
                    setFilters((prevState) => ({
                      ...prevState,
                      productId: value?.id ?? "",
                    }));
                  }}
                  value={
                    codeProduct
                      ? products.find((item) => item.id === codeProduct)
                      : null
                  }
                />
              </Grid>

              <Grid item xs={12} md={3} lg={2.4}>
                <SelectSubsidiaries
                  label="Filial"
                  valueSubsidiary={subsidiaries.find(
                    (item) => item.id === codeSubsidiary
                  )}
                  onChangeValue={(selectedOption) => {
                    if (selectedOption) {
                      setCodeSubsidiary(selectedOption.id);
                      setFilters((prevState) => ({
                        ...prevState,
                        subsidiaryId: selectedOption.id,
                      }));
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={3} lg={2.4} direction="row">
                <CalendarRange
                  label="Data de agendamento"
                  name="scheduleDate"
                  value={[null, null]}
                  clearValueCalendar={
                    !filters?.scheduleDateFrom && !filters?.scheduleDateTo
                  }
                  onChangeDateStartEnd={(dateBegin, dateEnd) => {
                    if (dateBegin !== null && dateEnd !== null) {
                      const formattedDateFrom = format(dateBegin, "yyyy-MM-dd");
                      const formattedDateTo = format(dateEnd, "yyyy-MM-dd");
                      setFilters((prevState) => ({
                        ...prevState,
                        scheduleDateFrom: formattedDateFrom,
                        scheduleDateTo: formattedDateTo,
                      }));
                    }
                  }}
                />
              </Grid>

              <Grid
                item
                container
                xs={12}
                lg={2.4}
                spacing={1}
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                sx={{ mt: 2 }}
              >
                <Grid item>
                  <CustomButton
                    title="Limpar filtros"
                    sx={{ textTransform: "unset" }}
                    onClick={handleClearFilters}
                    variant={CustomButtonVariant.OUTLINED}
                  />
                </Grid>
                <Grid item>
                  <CustomButton
                    title="Consultar"
                    onClick={handleFetchList}
                    sx={{ textTransform: "unset" }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {children}
          </CustomCard>
        </Grid>
      </Grid>
    </Layout>
  );
});

export { ProgramScheduleTable };
